/**
 * repAdvisor — pure decision-logic module for the AutomaticRep account-creation flow.
 *
 * This module is the single source of truth for the AutomaticRep rules. It is
 * side-effect free: it imports nothing (no React, Redux, or network), never mutates
 * its inputs, and every function returns a plain "patch" object ({} means "no change")
 * that the caller applies through its own state seam. It is defined once here in
 * bx-react-ui and consumed by both FinalizeAccountTab (internal) and AccountWizard
 * (in bx-onboarding, over the existing bx-onboarding -> bx-react-ui dependency edge).
 */

/**
 * True when the value is empty (undefined/null/blank) or represents the number zero.
 * Rep/Advisor ids may arrive as either numbers or strings, so both are handled.
 * @param {*} value
 * @returns {boolean}
 */
function isEmptyOrZero(value) {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === "number") {
    return value === 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return true;
    }
    return Number(trimmed) === 0;
  }
  return false;
}

/**
 * Loose id equality for Rep/Advisor identifiers that may be numbers or numeric
 * strings. Empty values never match. Falls back to numeric comparison so that
 * equivalent representations (e.g. "012" and 12) are treated as equal.
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function idEquals(a, b) {
  if (a === undefined || a === null || b === undefined || b === null) {
    return false;
  }
  const sa = String(a).trim();
  const sb = String(b).trim();
  if (sa === "" || sb === "") {
    return false;
  }
  if (sa === sb) {
    return true;
  }
  const na = Number(sa);
  const nb = Number(sb);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) {
    return na === nb;
  }
  return false;
}

/**
 * AutomaticRep is active only for exactly the number 1.
 *
 * Returns true if and only if the value is (or numerically represents) exactly 1.
 * The number 0, absent (null/undefined), empty string, booleans, objects, and any
 * non-numeric value all yield false.
 * @param {*} value the raw AutomaticRep parameter value
 * @returns {boolean}
 */
export function isAutomaticRepActive(value) {
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return false;
    }
    return Number(trimmed) === 1;
  }
  return false;
}

/**
 * Seed the account Rep from DefaultRepId, but only for an unset Rep, and never
 * overwrite a computed (non-zero) Rep.
 *
 * Returns a { RepId, RepName } patch only when ALL of the following hold:
 *   - the account RepId is empty or zero, AND
 *   - defaultRepId is non-zero, AND
 *   - defaultRepId is present in the reps payload.
 * In every other case it returns {} (no change), making it idempotent across
 * repeated application. The patch never contains MainAdvisorId or MainAdvisorName,
 * so no Main Advisor is ever derived from a global default here. Inputs are never
 * mutated.
 * @param {{ RepId?: * }} account
 * @param {Array<{ id?: *, name?: * }>} reps
 * @param {*} defaultRepId
 * @returns {{ RepId?: *, RepName?: * }}
 */
export function seedRepDefault(account, reps, defaultRepId) {
  const currentRepId = account ? account.RepId : undefined;

  // Never overwrite a Rep that is already set (computed Rep takes precedence).
  if (!isEmptyOrZero(currentRepId)) {
    return {};
  }

  // A zero/empty DefaultRepId is not a valid fallback.
  if (isEmptyOrZero(defaultRepId)) {
    return {};
  }

  // The fallback Rep must exist in the reps payload to resolve its name.
  const matchedRep = Array.isArray(reps)
    ? reps.find((rep) => rep && idEquals(rep.id, defaultRepId))
    : undefined;

  if (!matchedRep) {
    return {};
  }

  return {
    RepId: defaultRepId,
    RepName: matchedRep.name,
  };
}

/**
 * True when an advisor identified by advisorId is present in the given advisors
 * list. Empty/zero ids never match. The advisors list is the Rep-scoped
 * [{ id, name }] collection; a non-array is treated as empty.
 * @param {*} advisorId
 * @param {Array<{ id?: * }>} advisors
 * @returns {boolean}
 */
function advisorInList(advisorId, advisors) {
  if (isEmptyOrZero(advisorId) || !Array.isArray(advisors)) {
    return false;
  }
  return advisors.some((advisor) => advisor && idEquals(advisor.id, advisorId));
}

/**
 * Apply the persisted Rep getter response to the account.
 *
 * When the getter returns a valid (non-zero) RepId, the Rep is ALWAYS synced from
 * the backend as the source of truth (RepId + RepName). The Main Advisor follows
 * the Rep: it is overwritten from the getter ONLY when the resolved Rep changed;
 * when the backend returns the same Rep the account already holds, the current
 * advisor is preserved (the operator may have picked a different advisor within
 * that Rep). When getterData is missing or its RepId is zero/empty (also the
 * invariant for an error/timeout arrival) it returns {} so no field changes. The
 * patch never carries any field other than the four Rep/Advisor fields, so all
 * other account fields are left unchanged. Inputs are never mutated.
 * @param {{ RepId?: * }} account the current account (its RepId is compared)
 * @param {{ RepId?: *, RepName?: *, MainAdvisorId?: *, MainAdvisorName?: * }} getterData
 * @returns {{ RepId?: *, RepName?: *, MainAdvisorId?: *, MainAdvisorName?: * }}
 */
export function applyRepSync(account, getterData) {
  if (!getterData || isEmptyOrZero(getterData.RepId)) {
    return {};
  }

  // The Rep is always taken from the backend (source of truth).
  const patch = {
    RepId: getterData.RepId,
    RepName: getterData.RepName,
  };

  // The advisor follows the Rep: overwrite it only when the resolved Rep changed.
  // A same-Rep response keeps the account's current advisor.
  const currentRepId = account ? account.RepId : undefined;
  if (!idEquals(currentRepId, getterData.RepId)) {
    patch.MainAdvisorId = getterData.MainAdvisorId;
    patch.MainAdvisorName = getterData.MainAdvisorName;
  }

  return patch;
}

/**
 * Seed the Main Advisor from the resolved Rep's default advisor, but only when the
 * account has no advisor yet.
 *
 * Returns a { MainAdvisorId, MainAdvisorName } patch ONLY when BOTH:
 *   - the account's current MainAdvisorId is empty or zero, AND
 *   - the resolved Rep exposes a non-empty, non-zero defaultAdvisorId.
 * The advisor name is resolved from the Rep's advisors list (since the backend does
 * not return a defaultAdvisorName field). If the account already has a non-zero
 * advisor it is preserved (returns {}); if the Rep has no valid default advisor the
 * advisor is left unset (returns {}). Inputs are never mutated.
 * @param {{ MainAdvisorId?: * }} account
 * @param {{ defaultAdvisorId?: *, advisors?: Array<{ id?: *, name?: * }> }} resolvedRep
 * @returns {{ MainAdvisorId?: *, MainAdvisorName?: * }}
 */
export function resolveAdvisorSeed(account, resolvedRep) {
  if (!resolvedRep) {
    return {};
  }

  const currentAdvisorId = account ? account.MainAdvisorId : undefined;

  // Preserve an advisor that is already set (never clobber an existing selection).
  if (!isEmptyOrZero(currentAdvisorId)) {
    return {};
  }

  // The Rep must expose a valid default advisor to seed from.
  if (isEmptyOrZero(resolvedRep.defaultAdvisorId)) {
    return {};
  }

  // Resolve the advisor name from the Rep's advisors list.
  const advisors = Array.isArray(resolvedRep.advisors) ? resolvedRep.advisors : [];
  const matched = advisors.find((a) => a && idEquals(a.id, resolvedRep.defaultAdvisorId));

  return {
    MainAdvisorId: resolvedRep.defaultAdvisorId,
    MainAdvisorName: matched ? matched.name : "",
  };
}

/**
 * Re-scope the Main Advisor when the resolved Rep changes so that the resulting
 * MainAdvisorId is ALWAYS either empty or present in the new Rep's advisors list.
 *
 * Decision order:
 *   1. If the current MainAdvisorId is already in newRep.advisors, keep it (returns
 *      {} — no change).
 *   2. Otherwise, if newRep exposes a default advisor that is itself in
 *      newRep.advisors, reseed both fields from that default (guaranteed in-list).
 *      The advisor name is resolved from the advisors list.
 *   3. Otherwise, clear both MainAdvisorId and MainAdvisorName so no out-of-scope
 *      advisor remains.
 * Inputs are never mutated.
 * @param {{ MainAdvisorId?: * }} account
 * @param {{ advisors?: Array<{ id?: *, name?: * }>, defaultAdvisorId?: * }} newRep
 * @returns {{ MainAdvisorId?: *, MainAdvisorName?: * }}
 */
export function rescopeAdvisor(account, newRep) {
  const advisors = newRep && Array.isArray(newRep.advisors) ? newRep.advisors : [];
  const currentAdvisorId = account ? account.MainAdvisorId : undefined;

  // Keep the current advisor when it is already scoped to the new Rep.
  if (advisorInList(currentAdvisorId, advisors)) {
    return {};
  }

  // Reseed from the new Rep's default advisor when that default is itself in-list.
  const defaultAdvisorId = newRep ? newRep.defaultAdvisorId : undefined;
  if (advisorInList(defaultAdvisorId, advisors)) {
    const matched = advisors.find((a) => a && idEquals(a.id, defaultAdvisorId));
    return {
      MainAdvisorId: defaultAdvisorId,
      MainAdvisorName: matched ? matched.name : "",
    };
  }

  // Otherwise clear so no out-of-scope advisor selection persists.
  return {
    MainAdvisorId: "",
    MainAdvisorName: "",
  };
}
